import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './core/guard/auth.guard';
import {
  ALERT_ASSIGNMENT_CONFIG,
  INSPECTION_ASSIGNMENT_CONFIG,
} from './features/location-assignment/models/location-assignment-screen.config';
import { AssignAlertsHttpService } from './features/location-assignment/services/assign-alerts.http.service';
import { LocationGroupHttpService } from './features/location-assignment/services/location-group.http.service';
import { VenueEventHttpService } from './features/venue-event-configuration/services/venue-event.http.service';

export const routes: Routes = [
  { path: '', redirectTo: 'login', pathMatch: 'full' },

  {
    path: 'login',
    loadChildren: () => import('./features/login/login.module').then((m) => m.LoginModule),
  },
  {
    path: 'callback',
    loadChildren: () => import('./features/authcallback/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'landing/:destination',
    loadComponent: () =>
      import('./features/deeplink/landing/components/deeplink-landing.component').then(
        (m) => m.DeeplinkLandingComponent,
      ),
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./features/dashboard/dashboard.module').then((m) => m.DashboardModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'restroom-overview',
    loadChildren: () =>
      import('./features/restroomoverview/restroomoverview.module').then((m) => m.RestroomOverviewModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'overview-dashboard',
    loadChildren: () =>
      import('./features/overviewdashboard/overviewdashboard.module').then((m) => m.OverviewDashboardModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'predictive-analytics',
    loadChildren: () =>
      import('./features/restroomprediction/restroomprediction.module').then((m) => m.RestroomPredictionModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'service-provider',
    loadChildren: () =>
      import('./features/serviceprovider/serviceprovider.module').then((m) => m.ServiceProviderModule),
    canActivate: [AuthGuard],
  },

  //// User Management --------------------
  {
    path: 'user',
    loadChildren: () => import('./features/usermanagement/user/user.module').then((m) => m.UserModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'profile-menu',
    loadChildren: () => import('./features/usermanagement/user/user.module').then((m) => m.UserModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'user-role-permission-template',
    loadChildren: () =>
      import('./features/usermanagement/userroletemplate/userroletemplate.module').then(
        (m) => m.UserRoleTemplateModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'role',
    loadChildren: () => import('./features/usermanagement/role/role.module').then((m) => m.UserRoleModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'device-registration',
    loadChildren: () =>
      import('./features/usermanagement/deviceregistration/deviceregistration.module').then(
        (m) => m.DeviceRegistrationModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'subscription',
    loadChildren: () =>
      import('./features/usermanagement/subscription/subscription.module').then((m) => m.SubscriptionModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'userlocationassignment',
    loadComponent: () =>
      import('./features/location-assignment/components/assign-alerts/assign-alerts.component').then(
        (m) => m.AssignAlertsComponent,
      ),
    data: { screenConfig: ALERT_ASSIGNMENT_CONFIG },
    providers: [AssignAlertsHttpService],
    canActivate: [AuthGuard],
  },

  {
    path: 'share-user',
    loadChildren: () => import('./features/usermanagement/shareuser/shareuser.module').then((m) => m.ShareUserModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'acquired-users',
    loadChildren: () =>
      import('./features/usermanagement/acquireduser/acquireduser.module').then((m) => m.AcquiredUserModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'transfer-ownership',
    loadChildren: () =>
      import('./features/usermanagement/transferownership/transferownership.module').then(
        (m) => m.TransferOwnershipModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'terms-and-conditions',
    loadChildren: () =>
      import('./features/salesforcetermsconditions/salesforcetermsconditions.module').then(
        (m) => m.SalesforceTermsAndConditionsModule,
      ),
  },

  //// Staff Scheduling --------------------
  {
    path: 'shift',
    loadChildren: () => import('./features/staffscheduling/shift/shift.module').then((m) => m.ShiftModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'zone',
    loadChildren: () => import('./features/staffscheduling/zone/zone.module').then((m) => m.ZoneModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'beacon',
    loadChildren: () => import('./features/staffscheduling/beacon/beacon.module').then((m) => m.BeaconModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'location',
    loadChildren: () => import('./features/staffscheduling/location/location.module').then((m) => m.LocationModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'location-template',
    loadChildren: () =>
      import('./features/staffscheduling/locationtemplate/locationtemplate.module').then(
        (m) => m.LocationTemplateModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'location-type',
    loadChildren: () =>
      import('./features/staffscheduling/locationtype/locationtype.module').then((m) => m.LocationTypeModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'staff-schedule',
    loadChildren: () =>
      import('./features/staffscheduling/staffschedule/staffschedule.module').then((m) => m.StaffScheduleModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'shift-break-schedule',
    loadChildren: () =>
      import('./features/staffscheduling/shiftbreakschedule/shiftbreakschedule.module').then(
        (m) => m.ShiftScheduleModule,
      ),
    canActivate: [AuthGuard],
  },

  //// Venue Management --------------------
  {
    path: 'venue',
    loadChildren: () => import('./features/venuemanagement/venue/venue.module').then((m) => m.VenueModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'building',
    loadChildren: () => import('./features/venuemanagement/building/building.module').then((m) => m.BuildingModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'area',
    loadChildren: () => import('./features/venuemanagement/area/area.module').then((m) => m.AreaModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'level-assignment',
    loadChildren: () =>
      import('./features/venuemanagement/levelassignment/levelassignment.module').then((m) => m.LevelAssignmentModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'country',
    loadChildren: () => import('./features/venuemanagement/country/country.module').then((m) => m.CountryModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'state',
    loadChildren: () => import('./features/venuemanagement/state/state.module').then((m) => m.StateModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'city',
    loadChildren: () => import('./features/venuemanagement/city/city.module').then((m) => m.CityModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'gateway',
    loadChildren: () => import('./features/venuemanagement/gateway/getway.module').then((m) => m.GatewayModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'badge',
    loadChildren: () => import('./features/venuemanagement/badge/badge.module').then((m) => m.BadgeModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'gate',
    loadChildren: () => import('./features/venuemanagement/gate/gate.module').then((m) => m.GateModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'location-gate-group',
    loadChildren: () =>
      import('./features/venuemanagement/locationgategroup/locationgategroup.module').then(
        (m) => m.LocationGateGroupModule,
      ),
    canActivate: [AuthGuard],
  },

  // TODO: re-add `canActivate: [AuthGuard]` once the LOCATION_GROUP permission is returned in
  // the user's permissions array (pending API changes). Bypassed for now so it can be tested.
  {
    path: 'location-group',
    providers: [LocationGroupHttpService],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/location-assignment/components/location-group-list/location-group-list.component').then(
            (m) => m.LocationGroupListComponent,
          ),
      },
      {
        path: 'create',
        loadComponent: () =>
          import('./features/location-assignment/components/location-group-form/location-group-form.component').then(
            (m) => m.LocationGroupFormComponent,
          ),
      },
      {
        path: 'edit/:id',
        loadComponent: () =>
          import('./features/location-assignment/components/location-group-form/location-group-form.component').then(
            (m) => m.LocationGroupFormComponent,
          ),
      },
    ],
    canActivate: [AuthGuard],
  },

  {
    path: 'user-license-request-log',
    loadChildren: () =>
      import('./features/venuemanagement/userlicenserequestlog/userlicenserequestlog.module').then(
        (m) => m.UserLicenseRequestLogModule,
      ),
    canActivate: [AuthGuard],
  },

  //// Workorder Management --------------------
  {
    path: 'cleaning-alert',
    loadChildren: () =>
      import('./features/workordermanagement/workorder/workorder.module').then((m) => m.WorkorderModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'quality-check-log',
    loadChildren: () =>
      import('./features/workordermanagement/qualitychecklog/qualitychecklog.module').then(
        (m) => m.QualityCheckLogModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'task',
    loadChildren: () => import('./features/staffscheduling/task/task.module').then((m) => m.Task),
    canActivate: [AuthGuard],
  },

  {
    path: 'inventory',
    loadChildren: () => import('./features/staffscheduling/inventory/inventory.module').then((m) => m.InventoryModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'element',
    loadChildren: () => import('./features/staffscheduling/element/element.module').then((m) => m.ElementModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'venue-task-configuration',
    loadChildren: () =>
      import('./features/staffscheduling/venuetaskconfiguration/venuetaskconfiguration.module').then(
        (m) => m.VenueTask,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'venue-inventory-configuration',
    loadChildren: () =>
      import('./features/staffscheduling/venueinventoryconfiguration/venueinventoryconfiguration.module').then(
        (m) => m.VenueInventoryModule,
      ),
    canActivate: [AuthGuard],
  },

  // AuthGuard must stay: on a page refresh it restores the session (token, venue context, permissions).
  // The permission check itself is bypassed for now in AuthState.canAccess.
  {
    path: 'venue-event-configuration',
    providers: [VenueEventHttpService],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/venue-event-configuration/components/venue-event-list/venue-event-list.component').then(
            (m) => m.VenueEventListComponent,
          ),
      },
      {
        path: 'create',
        loadComponent: () =>
          import('./features/venue-event-configuration/components/venue-event-form/venue-event-form.component').then(
            (m) => m.VenueEventFormComponent,
          ),
      },
      {
        path: 'edit/:id',
        loadComponent: () =>
          import('./features/venue-event-configuration/components/venue-event-form/venue-event-form.component').then(
            (m) => m.VenueEventFormComponent,
          ),
      },
    ],
    canActivate: [AuthGuard],
  },

  {
    path: 'venue-element-configuration',
    loadChildren: () =>
      import('./features/staffscheduling/venueelementconfiguration/venueelementconfiguration.module').then(
        (m) => m.VenueElement,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'inspection-score-factor-configuration',
    loadChildren: () =>
      import('./features/staffscheduling/inspectionrating/inspectionrating.module').then(
        (m) => m.InspectionRatingModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'workorder',
    loadChildren: () =>
      import('./features/workordermanagement/workorder/maintenanceworkorder.module').then(
        (m) => m.MaintenanceWorkorderModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'workorder-type',
    loadChildren: () =>
      import('./features/workordermanagement/workordertype/workordertype.module').then((m) => m.WorkorderTypeModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'workorder-category',
    loadChildren: () =>
      import('./features/workordermanagement/workordercategory/workordercategory.module').then(
        (m) => m.WorkorderCategoryModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'closure-template',
    loadChildren: () =>
      import('./features/workordermanagement/closuretemplate/closuretemplate.module').then(
        (m) => m.ClosureTemplateModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'qc-workorder',
    loadChildren: () =>
      import('./features/workordermanagement/qcworkorder/qcworkorder.module').then((m) => m.QCWorkOrderModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'qc-cleaning-alert',
    loadChildren: () =>
      import('./features/workordermanagement/qccleaningalert/qccleaningalert.module').then(
        (m) => m.QCCleaningAlertModule,
      ),
    canActivate: [AuthGuard],
  },

  //// Asset Module --------------------
  {
    path: 'asset',
    loadChildren: () => import('./features/assetmanagement/asset/asset.module').then((m) => m.AssetModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'asset-type',
    loadChildren: () => import('./features/assetmanagement/assettype/assettype.module').then((m) => m.AssetTypeModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'product',
    loadChildren: () => import('./features/assetmanagement/product/product.module').then((m) => m.ProductModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'product-model',
    loadChildren: () =>
      import('./features/assetmanagement/productmodel/productmodel.module').then((m) => m.ProductModelModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'product-manufacturer',
    loadChildren: () =>
      import('./features/assetmanagement/productmanufacturer/productmanufacturer.module').then(
        (m) => m.ProductManufacturerModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'supplier',
    loadChildren: () => import('./features/assetmanagement/supplier/supplier.module').then((m) => m.SupplierModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'import-data',
    loadChildren: () => import('./features/importdata/importdata.module').then((m) => m.ImportDataModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'sensor',
    loadChildren: () => import('./features/sensor/sensor.module').then((m) => m.SensorModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'stall-occupancy-status',
    loadChildren: () =>
      import('./features/corporationmanagement/stallocupancystatus/stallocupancystatus.module').then(
        (m) => m.StallOcupancyStatusModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'corporation',
    loadChildren: () =>
      import('./features/corporationmanagement/corporation/corporation.module').then((m) => m.CorporationModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'alert-template',
    loadChildren: () =>
      import('./features/corporationmanagement/alerttemplate/alerttemplate.module').then((m) => m.AlertTemplateModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'configure-default-venue',
    loadChildren: () =>
      import('./features/usermanagement/configuredefaultvenue/configuredefaultvenue.module').then(
        (m) => m.ConfigureDefaultVenueModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'alert-type',
    loadChildren: () =>
      import('./features/corporationmanagement/alerttype/alerttype.module').then((m) => m.AlertTypeModule),
    canActivate: [AuthGuard],
  },

  //// venue survey configuration --------------------
  {
    path: 'survey-question-configuration',
    loadChildren: () =>
      import('./features/venuesurveyconfigurations/surveyquestionconfiguration/surveyquestionconfiguration.module').then(
        (m) => m.SurveyQuestionConfigModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'survey-question-response-configuration',
    loadChildren: () =>
      import('./features/venuesurveyconfigurations/surveyquestionresponseconfiguration/surveyquestionresponseconfiguration.module').then(
        (m) => m.SurveyQuestionResponseConfigModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'inspection-dashboard',
    loadChildren: () =>
      import('./features/inspectiondashboard/inspectiondashboard.module').then((m) => m.InspectionDashboardModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'statistics',
    loadChildren: () =>
      import('./features/statisticsdashboard/statisticsdashboard.module').then((m) => m.StatisticsDashboardModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'signage/:id',
    loadChildren: () => import('./features/signage/signage.module').then((m) => m.SignageModule),
  },

  {
    path: 'signage-atl/:id',
    loadChildren: () =>
      import('./features/digitaldisplay/signage-atl/signage-atl.module').then((m) => m.SignageATLModule),
  },

  {
    path: 'signage-dfw',
    loadChildren: () =>
      import('./features/digitaldisplay/signage-dfw/signage-dfw.module').then((m) => m.SignageDFWModule),
  },

  {
    path: 'signage-dfw/:id',
    loadChildren: () =>
      import('./features/digitaldisplay/signage-dfw/signage-dfw.module').then((m) => m.SignageDFWModule),
  },

  //// Inspection Management --------------------
  {
    path: 'inspection-log',
    loadChildren: () =>
      import('./features/workordermanagement/inspectionlog/inspectionlog.module').then((m) => m.InspectionModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'inspection',
    loadChildren: () =>
      import('./features/inspectionmanagement/inspection/inspection.module').then((m) => m.InspectionDetailModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'inspectorlocationassignment',
    loadComponent: () =>
      import('./features/location-assignment/components/assign-alerts/assign-alerts.component').then(
        (m) => m.AssignAlertsComponent,
      ),
    data: { screenConfig: INSPECTION_ASSIGNMENT_CONFIG },
    providers: [AssignAlertsHttpService],
    canActivate: [AuthGuard],
  },

  {
    path: 'location-overview',
    loadChildren: () =>
      import('./features/inspectionmanagement/locationoverview/locationoverview.module').then(
        (m) => m.LocationOverviewModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'inspection-notification-alert',
    loadChildren: () =>
      import('./features/inspectionmanagement/inspectionnotification/inspectionnotification.module').then(
        (m) => m.InspectionNotificationModule,
      ),
    canActivate: [AuthGuard],
  },

  //// Project Work --------------------
  {
    path: 'project-work-log',
    loadChildren: () =>
      import('./features/projectwork/projectworklog/projectworklog.module').then((m) => m.ProjectWorkLogModule),
    canActivate: [AuthGuard],
  },

  //// Reports --------------------
  {
    path: 'throughput-custom-report',
    loadChildren: () =>
      import('./features/reports/customreports/throughput/throughputcustomreport.module').then(
        (m) => m.ThroughputCustomReportModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'feedback-custom-report',
    loadChildren: () =>
      import('./features/reports/customreports/feedback/feedbackcustomreport.module').then(
        (m) => m.FeedbackCustomReportModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'inspection-log-report',
    loadChildren: () =>
      import('./features/reports/customreports/inspectionlogreport/inspectionlogreport.module').then(
        (m) => m.InspectionLogReportModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'dispenser-custom-report',
    loadChildren: () =>
      import('./features/reports/customreports/dispensors/dispensorscustomreport.module').then(
        (m) => m.DispensorsCustomReportModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'task-custom-report',
    loadChildren: () =>
      import('./features/reports/customreports/task/taskcustomreport.module').then((m) => m.TaskCustomReportModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'inventory-custom-report',
    loadChildren: () =>
      import('./features/reports/customreports/inventory/inventorycustomreport.module').then(
        (m) => m.InventoryCustomReportModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'trash-custom-report',
    loadChildren: () =>
      import('./features/reports/customreports/trash/trashcustomreport.module').then((m) => m.TrashCustomReportModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'badge-summary',
    loadChildren: () =>
      import('./features/reports/customreports/badgesummary/badgesummaryreport.module').then(
        (m) => m.BadgeSummaryReportModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'cleaning-alert-custom-report',
    loadChildren: () =>
      import('./features/reports/customreports/cleaningalert/cleaningalertcustomreport.module').then(
        (m) => m.CleaningAlertCustomReportModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'user-log-activity-custom-report',
    loadChildren: () =>
      import('./features/reports/customreports/userloginactivity/userloginactivitycustomreport.module').then(
        (m) => m.UserLoginActivityCustomReportModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'flush-valve-custom-report',
    loadChildren: () =>
      import('./features/reports/customreports/zurnsmartplumbing/zurnsmartplumbingcustomreport.module').then(
        (m) => m.ZurnSmartPlumbingCustomReportModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'equipment-pm-custom-report',
    loadChildren: () =>
      import('./features/reports/customreports/events/eventscustomreport.module').then(
        (m) => m.EventsCustomReportModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'user-billing-custom-report',
    loadChildren: () =>
      import('./features/reports/customreports/userbillingreport/userbillingcustomreport.module').then(
        (m) => m.UserBillingcCustomReportModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'project-work-custom-report',
    loadChildren: () =>
      import('./features/reports/customreports/projectworklogreport/projectworklogreport.module').then(
        (m) => m.ProjectWorkLogReportModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'calender-schedules-custom-report',
    loadChildren: () =>
      import('./features/reports/customreports/calendarschedulesreport/calendarschedulesreport.module').then(
        (m) => m.CalendarScheduleReportModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'calendar',
    loadChildren: () => import('./features/calendar/calendar.module').then((m) => m.CalModule),
    canActivate: [AuthGuard],
  },

  // Touchless Feedback Configuration  --------------------
  {
    path: 'feedback-configuration',
    loadChildren: () =>
      import('./features/touchlessfeedbackconfiguration/touchless-feedback-config/touchless-feedback-config.module').then(
        (m) => m.TouchlessFeedbackConfigModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'venue-feedback-reason',
    loadChildren: () =>
      import('./features/touchlessfeedbackconfiguration/touchless-feedback-reason/touchless-feedback-reason.module').then(
        (m) => m.TouchlessFeedbackReasonModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'trax-iq',
    loadChildren: () => import('./features/trax-iq/trax-iq.module').then((m) => m.TraxIQModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'alert-type-configuration',
    loadChildren: () =>
      import('./features/venuemanagement/alerttypeconfiguration/alerttypeconfiguration.module').then(
        (m) => m.AlertTypeConfigurationModule,
      ),
    canActivate: [AuthGuard],
  },
];

export const AppRoutes = RouterModule.forRoot(routes, { useHash: true, onSameUrlNavigation: 'reload' });
